import { useCallback } from "react";
import { useHttpAdapter } from "./useHttpAdapter";
import {
  WeeklyPlan,
  WeeklyPlanItem,
  WeeklyPlanItemResponse,
  WeeklyPlanItemUpdate,
  WeeklyPlanResponse,
} from "../types/weekly-plan";
import { normalizeSocialPlatformId } from "../utils/platform-post-util";

const normalizeWeeklyPlanItem = (item: WeeklyPlanItem): WeeklyPlanItem => ({
  ...item,
  platform: normalizeSocialPlatformId(item.platform),
});

const normalizeWeeklyPlan = (plan: WeeklyPlan | null): WeeklyPlan | null => {
  if (!plan) {
    return null;
  }

  return {
    ...plan,
    items: plan.items.map(normalizeWeeklyPlanItem),
  };
};

const useWeeklyPlanApi = () => {
  const { get, post, patch, del } = useHttpAdapter();

  const getWeeklyPlan = useCallback(
    async (brandId: string, weekStart: string): Promise<WeeklyPlan | null> => {
      try {
        const response = await get<WeeklyPlanResponse>(
          `/weekly-plans/${brandId}?week_start=${encodeURIComponent(weekStart)}`
        );
        return normalizeWeeklyPlan(response.data.data);
      } catch {
        return null;
      }
    },
    [get]
  );

  const generateWeeklyPlan = useCallback(
    async (
      brandId: string,
      weekStart: string,
      timezone: string
    ): Promise<WeeklyPlan | null> => {
      try {
        const response = await post<WeeklyPlanResponse>(
          `/weekly-plans/${brandId}/generate`,
          {
            week_start: weekStart,
            timezone,
          }
        );
        return normalizeWeeklyPlan(response.data.data);
      } catch {
        return null;
      }
    },
    [post]
  );

  const updateWeeklyPlanItem = useCallback(
    async (
      itemId: string,
      updates: WeeklyPlanItemUpdate
    ): Promise<WeeklyPlanItem | null> => {
      try {
        const response = await patch<WeeklyPlanItemResponse>(
          `/weekly-plans/items/${itemId}`,
          updates
        );
        return normalizeWeeklyPlanItem(response.data.data);
      } catch {
        return null;
      }
    },
    [patch]
  );

  const deleteWeeklyPlanItem = useCallback(
    async (itemId: string): Promise<boolean> => {
      try {
        await del(`/weekly-plans/items/${itemId}`);
        return true;
      } catch {
        return false;
      }
    },
    [del]
  );

  return {
    getWeeklyPlan,
    generateWeeklyPlan,
    updateWeeklyPlanItem,
    deleteWeeklyPlanItem,
  };
};

export default useWeeklyPlanApi;
